import { getDrivers } from "@services";
import { FleetDriver } from "@types";
import React, { useEffect, useMemo, useState } from "react";

export type UserCoords = { lat?: number; lon?: number };

export type TCoreContext = {
  drivers: FleetDriver[];
  isUnauthorized: boolean;
  isLoading: boolean;
  userCoords: UserCoords;
};

export const CoreContext = React.createContext<TCoreContext>({
  drivers: [],
  isUnauthorized: false,
  isLoading: true,
  userCoords: {},
});

export const CoreProvider = ({ children }: Readonly<{ children: React.JSX.Element }>) => {
  const [drivers, setDrivers] = useState<FleetDriver[]>([]);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userCoords, setUserCoords] = useState<UserCoords>({});

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      () => {
        setUserCoords({});
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await getDrivers();
      setDrivers(response);
    } catch (error: any) {
      setDrivers([]);

      const hasInsufficientRole =
        (error?.response?.status === 403 &&
          (error?.response?.data?.code === "INSUFFICIENT_ROLE" ||
            error?.response?.data?.code === "ACCOUNT_NOT_ALLOWED")) ||
        (window as any).insufficientRole;

      if (hasInsufficientRole) {
        setIsUnauthorized(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();

    const handleUnauthorizedEvent = () => {
      setIsUnauthorized(true);
    };

    window.addEventListener("app:unauthorized", handleUnauthorizedEvent);
    return () => {
      window.removeEventListener("app:unauthorized", handleUnauthorizedEvent);
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      drivers,
      isUnauthorized,
      isLoading,
      userCoords,
    }),
    [drivers, isUnauthorized, isLoading, userCoords]
  );

  return <CoreContext.Provider value={contextValue}>{children}</CoreContext.Provider>;
};
